import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Printer } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { AppHeader } from "@/components/AppHeader";
import { api, API } from "@/lib/api";

const Row = ({ label, value, testId }) => (
  <div className="flex gap-3 border-b border-zinc-800 py-1.5 last:border-0">
    <span className="w-32 shrink-0 text-[10px] uppercase tracking-widest text-zinc-500">
      {label}
    </span>
    <span className="text-sm" data-testid={testId}>
      {value || "—"}
    </span>
  </div>
);

const Block = ({ title, children, testId }) => (
  <section className="mt-4 border border-zinc-800 p-3" data-testid={testId}>
    <h2 className="mb-1 text-xs font-bold uppercase tracking-widest text-zinc-400">
      {title}
    </h2>
    {children}
  </section>
);

export default function JobPacket() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [pieces, setPieces] = useState([]);
  const [sinks, setSinks] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [approval, setApproval] = useState(null);
  const [seams, setSeams] = useState(null);

  useEffect(() => {
    api
      .get(`/jobs/${id}/seams`)
      .then(({ data }) => setSeams(data))
      .catch(() => setSeams(null));
    api.get(`/jobs/${id}`).then(({ data }) => {
      setJob(data);
      if (data.customer_id)
        api
          .get(`/customers/${data.customer_id}`)
          .then((r) => setCustomer(r.data))
          .catch(() => setCustomer(null));
    });
    api
      .get(`/jobs/${id}/pieces`)
      .then(({ data }) => setPieces(data.items || []))
      .catch(() => setPieces([]));
    api
      .get("/sink-allocations", { params: { entity: "job", entity_id: id } })
      .then(({ data }) => setSinks(data.items || []))
      .catch(() => setSinks([]));
    api
      .get(`/jobs/${id}/drawing-approval`)
      .then(({ data }) => setApproval(data))
      .catch(() => setApproval(null));
  }, [id]);

  if (!job)
    return (
      <div className="grain min-h-screen">
        <AppHeader title="Install packet" />
        <main className="relative z-10 mx-auto max-w-3xl px-4 pt-10 text-zinc-500">
          Loading packet…
        </main>
      </div>
    );

  const schedule = [
    ["Template", job.template_date, job.template_time, job.template_crew],
    ["Fabrication", job.fab_date, job.fab_time, job.fab_crew],
    ["Install", job.install_date, job.install_time, job.install_crew],
  ];

  return (
    <div className="grain min-h-screen">
      <AppHeader title="Install packet" />
      <main className="print-sheet relative z-10 mx-auto max-w-3xl px-4 pb-24 pt-6">
        <div className="no-print mb-4 flex items-center gap-2">
          <button
            onClick={() => navigate(`/projects/${id}?tab=field`)}
            data-testid="packet-back-button"
            className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white"
          >
            <ArrowLeft size={16} weight="bold" /> Back to job
          </button>
          <Button
            data-testid="packet-print-button"
            onClick={() => window.print()}
            className="ml-auto h-11 bg-indigo-500 text-[11px] font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
          >
            <Printer size={15} weight="bold" className="mr-2" /> Print packet
          </Button>
        </div>

        <div
          className="border border-zinc-800 bg-zinc-900 p-5"
          data-testid="job-packet"
        >
          <div className="flex items-start justify-between gap-4 border-b border-zinc-700 pb-3">
            <div>
              <div className="text-xs font-bold uppercase tracking-widest text-zinc-400">
                Brioni Granite — Install Packet
              </div>
              <h1
                className="mt-1 text-xl font-bold"
                data-testid="packet-project"
              >
                {job.project_name}
              </h1>
              <div className="text-sm text-zinc-400">
                {job.customer_name || "No customer"}
              </div>
            </div>
            <div className="text-right">
              <div className="font-mono-data text-sm" data-testid="packet-code">
                {job.code}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                {job.stage}
              </div>
            </div>
          </div>

          <Block title="Site & Contact" testId="packet-site">
            <Row label="Address" value={job.address} testId="packet-address" />
            <Row label="Phone" value={customer?.phone} />
            <Row label="Email" value={customer?.email} />
            {approval?.has_drawing && (
              <Row
                label="Drawing"
                testId="packet-drawing-approval"
                value={
                  approval.status === "approved"
                    ? `Approved by ${approval.name} on ${new Date(
                        approval.at,
                      ).toLocaleDateString()}`
                    : "NOT APPROVED by the customer"
                }
              />
            )}
          </Block>

          <Block title="Schedule" testId="packet-schedule">
            {schedule.map(([label, date, time, crew]) => (
              <Row
                key={label}
                label={label}
                testId={`packet-schedule-${label.toLowerCase()}`}
                value={
                  date
                    ? `${date}${time ? ` at ${time}` : ""}${crew ? ` · ${crew}` : ""}`
                    : "Not scheduled"
                }
              />
            ))}
          </Block>

          <Block title="Sink" testId="packet-sink">
            <Row label="Location" value={job.sink_location} />
            <Row label="Notes" value={job.sink_notes} />
            <Row
              label="From stock"
              testId="packet-sink-stock"
              value={
                sinks.length === 0
                  ? "None reserved"
                  : sinks
                      .map(
                        (s) =>
                          `${[s.brand, s.model].filter(Boolean).join(" ")} x${s.qty || 1}`,
                      )
                      .join(", ")
              }
            />
          </Block>

          <Block title="Material" testId="packet-material">
            {pieces.length === 0 && (job.material_links || []).length === 0 && (
              <p className="text-sm text-zinc-500">No material assigned.</p>
            )}
            {pieces.length > 0 && (
              <div className="mb-2 grid grid-cols-2 gap-2 sm:grid-cols-3" data-testid="packet-piece-photos">
                {pieces.map((p) => (
                  <figure
                    key={`photo-${p.piece_id}`}
                    data-testid={`packet-piece-card-${p.piece_id}`}
                    className="overflow-hidden border border-zinc-800"
                  >
                    {p.thumb_path ? (
                      <img
                        src={`${API}/photos/${p.thumb_path}?auth=${localStorage.getItem("sw_token")}&w=600`}
                        alt={p.serial_number || p.slab_color}
                        className="h-28 w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-28 items-center justify-center text-[10px] uppercase tracking-widest text-zinc-600">
                        No photo
                      </div>
                    )}
                    <figcaption className="px-2 py-1.5 text-xs">
                      <div className="font-mono-data font-bold">
                        {p.serial_number ? `SN ${p.serial_number}` : p.name || p.slab_color}
                      </div>
                      <div className="text-zinc-400">
                        {[p.slab_color, p.size, p.location && `Rack ${p.location}`]
                          .filter(Boolean)
                          .join(" · ")}
                      </div>
                    </figcaption>
                  </figure>
                ))}
              </div>
            )}
            {pieces.map((p) => (
              <Row
                key={p.piece_id}
                label={p.serial_number ? `SN ${p.serial_number}` : "Slab"}
                testId={`packet-piece-${p.piece_id}`}
                value={`${p.name || p.slab_color} · ${p.sqft} ft²${
                  p.lot_number ? ` · lot ${p.lot_number}` : ""
                }${p.bundle_number ? ` · bundle ${p.bundle_number}` : ""}${
                  p.location ? ` · rack ${p.location}` : ""
                }`}
              />
            ))}
            {(job.material_links || [])
              .filter((l) => l.type !== "piece")
              .map((l) => (
              <Row
                key={`${l.type}:${l.id}`}
                label={l.code}
                value={l.label}
                testId={`packet-link-${l.id}`}
              />
            ))}
          </Block>

          {seams?.enabled && (seams.seams || []).length > 0 && (
            <Block title={`Seams (${seams.seams.length})`} testId="packet-seams">
              {seams.seams.map((s, i) => (
                <Row
                  key={`${s.a}-${s.b}-${i}`}
                  label={s.len_text || `${s.len}″`}
                  value={`${s.area} · ${s.label}`}
                  testId={`packet-seam-${i}`}
                />
              ))}
            </Block>
          )}

          {job.templator_template && (
            <Block title="Field Template (LPI Cloud)" testId="packet-template">
              <Row
                label="Pieces"
                testId="packet-template-count"
                value={`${job.templator_template.piece_count} · ${job.templator_template.total_sqft} ft² total`}
              />
              {(job.templator_template.pieces || []).map((p, i) => (
                <Row
                  key={i}
                  label={p.label || `Piece ${i + 1}`}
                  testId={`packet-template-piece-${i}`}
                  value={`${p.length_in}" × ${p.width_in}" · ${p.sqft} ft²${
                    p.cutouts
                      ? ` · ${p.cutouts} cut-out${p.cutouts > 1 ? "s" : ""}`
                      : ""
                  }`}
                />
              ))}
            </Block>
          )}

          <Block title="Job Summary & Notes" testId="packet-summary">
            <p className="whitespace-pre-wrap text-sm text-zinc-200">
              {job.summary || "—"}
            </p>
            {(job.notes || []).slice(-3).map((n) => (
              <p
                key={n.id}
                className="mt-2 border-t border-zinc-800 pt-2 text-xs text-zinc-400"
              >
                {n.body}
              </p>
            ))}
          </Block>

          <div className="mt-5 grid grid-cols-2 gap-6 border-t border-zinc-700 pt-4 text-[10px] uppercase tracking-widest text-zinc-500">
            <div>
              <div className="mb-6">Crew signature</div>
              <div className="border-t border-zinc-600" />
            </div>
            <div>
              <div className="mb-6">Customer signature / date</div>
              <div className="border-t border-zinc-600" />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
